import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const money = (n) => Number(n || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
const fmtD = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

const STATUSES = ["Under budget", "On target", "Over budget", "No funding"];
const COLOURS = { "Under budget": "#0ea5e9", "On target": "#059669", "Over budget": "#dc2626", "No funding": "#9f1239" };
const PILL = {
  "Under budget": "bg-sky-50 text-sky-700 border-sky-200",
  "On target": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Over budget": "bg-rose-50 text-rose-700 border-rose-200",
  "No funding": "bg-rose-50 text-rose-700 border-rose-200",
};
const RANK = { "No funding": 0, "Over budget": 1, "Under budget": 2, "On target": 3 };

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

// Dashboard summary of service agreement budgets. The per-line detail lives on each
// participant's Rostering > Budget page, linked from every row.
export function BudgetReport({ includeInactive, search }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["ndis-management-budget", includeInactive],
    queryFn: () => api.ndisManagementBudget({ include_inactive: includeInactive }),
  });
  const rows = useMemo(() => {
    const t = (search || "").trim().toLowerCase();
    const f = (data?.tracked || []).filter((r) => !t || r.name.toLowerCase().includes(t));
    return [...f].sort((a, b) => (Number(b.needs_new_agreement.length > 0) - Number(a.needs_new_agreement.length > 0))
      || (RANK[a.status] - RANK[b.status]) || a.name.localeCompare(b.name));
  }, [data, search]);
  const chart = useMemo(() => STATUSES.map((s) => ({ label: s, count: (data?.tracked || []).filter((r) => r.status === s).length })), [data]);

  if (isLoading) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (isError) return <div className="p-6 text-sm text-rose-600" data-testid="ndis-budget-error">Couldn't load the budget report.</div>;

  const tracked = data.tracked;
  const need = tracked.filter((r) => r.needs_new_agreement.length > 0);
  const over = tracked.filter((r) => r.status === "Over budget" || r.status === "No funding");
  const under = tracked.filter((r) => r.status === "Under budget");

  return (
    <div className="space-y-4" data-testid="ndis-budget-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Participants tracked" value={tracked.length} />
        <Stat label="Need a new agreement" value={need.length} tone={need.length ? "text-rose-700" : undefined} />
        <Stat label="Over budget" value={over.length} tone={over.length ? "text-rose-700" : undefined} />
        <Stat label="Under budget" value={under.length} tone="text-sky-700" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-1">
          <div className="text-sm font-semibold text-slate-700 mb-2">Participants by budget status</div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ left: -16, right: 8, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>{chart.map((d) => <Cell key={d.label} fill={COLOURS[d.label]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Status compares everything invoiced, approved or rostered to the end of each agreement with its total.</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2">
          <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-amber-500" />Participants needing a new agreement ({need.length})</div>
          {need.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">None flagged.</div> : (
            <ul className="divide-y divide-slate-100 text-sm max-h-52 overflow-y-auto">
              {need.map((r) => (
                <li key={r.id} className="py-1.5 flex flex-wrap justify-between gap-2">
                  <Link to={`/participants?open=${encodeURIComponent(r.id)}&tab=budget`} className="font-medium text-brand hover:underline">{r.name}</Link>
                  <span className="text-slate-500 text-xs">{r.needs_new_agreement.join(", ")}{r.flagged ? ` · ${r.flagged} flagged shift${r.flagged === 1 ? "" : "s"}` : ""}</span>
                </li>))}
            </ul>)}
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="ndis-budget-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>{["Participant", "Agreement ends", "Total", "Invoiced", "Committed", "Remaining", "Time elapsed", "Projected", "Status"].map((h) => <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ndis-budget-row-${r.id}`}>
                <td className="px-3 py-2 font-medium whitespace-nowrap"><Link to={`/participants?open=${encodeURIComponent(r.id)}&tab=budget`} className="text-slate-800 hover:text-brand hover:underline">{r.name}</Link></td>
                <td className="px-3 py-2 whitespace-nowrap">{fmtD(r.sa_end_date)}</td>
                <td className="px-3 py-2 tabular-nums">{money(r.total)}</td>
                <td className="px-3 py-2 tabular-nums">{money(r.invoiced)}</td>
                <td className="px-3 py-2 tabular-nums">{money(r.committed)}</td>
                <td className={`px-3 py-2 tabular-nums ${r.remaining < 0 ? "text-rose-700 font-semibold" : ""}`}>{money(r.remaining)}</td>
                <td className="px-3 py-2 tabular-nums">{r.elapsed_percent}%</td>
                <td className="px-3 py-2 tabular-nums">{r.projected_percent == null ? "—" : `${r.projected_percent}%`}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`inline-flex items-center text-[10px] font-medium border rounded-full px-2 py-0.5 ${PILL[r.status] || ""}`}>{r.status}</span>
                  {r.needs_new_agreement.length > 0 && <span className="ml-1.5 text-[10px] font-medium text-rose-600">new agreement</span>}
                </td>
              </tr>))}
            {!rows.length && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No participants with a tracked agreement.</td></tr>}
          </tbody>
        </table>
      </div>

      {data.untracked.length > 0 && (
        <details className="rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid="ndis-budget-untracked">
          <summary className="cursor-pointer text-slate-600">{data.untracked.length} participant{data.untracked.length === 1 ? "" : "s"} without budget tracking (no Active agreement built in this system)</summary>
          <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
            {data.untracked.map((u) => <Link key={u.id} to={`/participants?open=${encodeURIComponent(u.id)}`} className="hover:text-brand hover:underline">{u.name}</Link>)}
          </div>
        </details>)}
    </div>
  );
}
